export function isValidReturnUrl(
  url: string | null,
  origin = window.location.origin
): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url, origin);
    return parsed.origin === origin;
  } catch {
    return false;
  }
}

/** True when `url` differs from `currentUrl` at most by its hash. */
export function isSameDocumentUrl(
  url: string,
  currentUrl = window.location.href
): boolean {
  try {
    const target = new URL(url, currentUrl);
    const current = new URL(currentUrl);
    return (
      target.origin === current.origin &&
      target.pathname === current.pathname &&
      target.search === current.search
    );
  } catch {
    return false;
  }
}

interface UrlServiceLike {
  match(criteria: { path: string }): {
    rule?: { type: string; state?: { name: string } };
  };
  url(path: string): void;
  sync(): void;
}

interface StateServiceLike {
  current: { name?: string };
}

interface WindowLike {
  location: { href: string; assign(url: string): void };
}

/**
 * A same-document returnUrl is a ui-router route, not a page load:
 * location.href would leave us on /auth with the spinner up. Returns false
 * when the route goes nowhere, so callers fall back to their normal
 * destination.
 */
export function followReturnUrl(
  $urlService: UrlServiceLike,
  $state: StateServiceLike,
  $window: WindowLike,
  returnUrl: string
): boolean {
  if (!isValidReturnUrl(returnUrl)) {
    return false;
  }

  if (!isSameDocumentUrl(returnUrl)) {
    $window.location.assign(returnUrl);
    return true;
  }

  const path = new URL(returnUrl, $window.location.href).hash.replace(
    /^#!/,
    ''
  );
  const match = $urlService.match({ path });
  if (
    match?.rule?.type !== 'STATE' ||
    match.rule.state?.name === $state.current.name
  ) {
    return false;
  }

  $urlService.url(path);
  $urlService.sync();
  return true;
}
